import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { inviteAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './ui/alert-dialog';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Mail, 
  Copy, 
  Trash2, 
  Clock, 
  CheckCircle,
  Loader2,
  Crown,
  User,
  RefreshCw,
  FileText,
  ExternalLink
} from 'lucide-react';
import { Textarea } from './ui/textarea';
import InviteStats from './InviteStats';

export default function TeamManager({ projectId, projectName }) {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [newInviteLink, setNewInviteLink] = useState(null);
  const [emailContentOpen, setEmailContentOpen] = useState(false);
  const [emailContent, setEmailContent] = useState(null);
  const [loadingEmailContent, setLoadingEmailContent] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    loadTeamData();
  }, [projectId]);

  const loadTeamData = async () => {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        inviteAPI.getProjectMembers(projectId),
        inviteAPI.getProjectInvites(projectId),
      ]);
      setMembers(membersRes.data.members || []);
      setInvites(invitesRes.data.invites || []);
    } catch (error) {
      console.error('Failed to load team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }

    setInviting(true);
    try {
      const res = await inviteAPI.inviteToProject(projectId, { email: inviteEmail });
      setNewInviteLink(res.data.invite_url);
      setInvites([...invites, res.data.invite]);
      
      if (res.data.email_sent) {
        toast.success('Invitation sent via email!');
      } else {
        toast.warning('Invitation created! Email delivery may be delayed - share the link directly.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Failed to send invitation';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || errorMsg;
      }
      toast.error(errorMsg);
    } finally {
      setInviting(false);
    }
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await inviteAPI.cancelInvite(projectId, inviteId);
      setInvites(invites.filter(i => i.id !== inviteId));
      toast.success('Invitation cancelled');
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await inviteAPI.removeMember(projectId, memberId);
      setMembers(members.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const resetInviteDialog = () => {
    setInviteEmail('');
    setNewInviteLink(null);
    setInviteOpen(false);
  };

  const handleResendInvite = async (inviteId) => {
    setResendingId(inviteId);
    try {
      const res = await inviteAPI.resendInvite(projectId, inviteId);
      // Update the invite in the list with new expiration
      setInvites(invites.map(i => 
        i.id === inviteId 
          ? { ...i, expires_at: res.data.expires_at, invite_url: res.data.invite_url }
          : i
      ));
      if (res.data.email_sent) {
        toast.success('Invitation resent via email!');
      } else {
        toast.warning('New link generated! Email delivery may be delayed - share the link directly.');
      }
    } catch (error) {
      toast.error('Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  const handleShowEmailContent = async (inviteId) => {
    setLoadingEmailContent(true);
    setEmailContentOpen(true);
    try {
      const res = await inviteAPI.getEmailContent(projectId, inviteId);
      setEmailContent(res.data);
    } catch (error) {
      toast.error('Failed to load email content');
      setEmailContentOpen(false);
    } finally {
      setLoadingEmailContent(false);
    }
  };

  const copyEmailContent = () => {
    if (emailContent) {
      navigator.clipboard.writeText(emailContent.plain_text);
      toast.success('Email content copied!');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-outfit text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Team
          </h2>
          <p className="text-zinc-600 mt-1">Manage team members and invitations</p>
        </div>
        
        <div className="flex items-center gap-2">
          <InviteStats projectId={projectId} />
          
          <Dialog open={inviteOpen} onOpenChange={(open) => {
            if (!open) resetInviteDialog();
            else setInviteOpen(true);
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="invite-member-btn"
                className="bg-primary hover:bg-orange-600 text-white rounded-xl"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit">Invite Team Member</DialogTitle>
              <DialogDescription>
                Send an invitation to join "{projectName}"
              </DialogDescription>
            </DialogHeader>
            
            {newInviteLink ? (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
                <div className="text-center">
                  <p className="font-medium text-zinc-900">Invitation sent!</p>
                  <p className="text-sm text-zinc-600 mt-1">
                    An email has been sent to <strong>{inviteEmail}</strong>
                  </p>
                </div>
                <div className="bg-zinc-100 p-3 rounded-lg">
                  <p className="text-xs text-zinc-500 mb-2">Or share this link directly:</p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={newInviteLink}
                      readOnly
                      className="text-xs bg-white"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopyLink(newInviteLink)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <Button onClick={resetInviteDialog} className="w-full rounded-xl">
                  Done
                </Button>
              </div>
            ) : (
              <form onSubmit={handleInvite} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email Address</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    data-testid="invite-email-input"
                    className="rounded-xl"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetInviteDialog}
                    className="rounded-lg"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={inviting}
                    data-testid="send-invite-btn"
                    className="bg-primary hover:bg-orange-600 text-white rounded-lg"
                  >
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Invitation'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Team Members */}
      <Card className="bg-white border-zinc-200 rounded-xl p-6">
        <h3 className="font-outfit font-semibold text-lg text-zinc-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Members ({members.length})
        </h3>
        
        {members.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <Users className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
            <p>No team members yet</p>
            <p className="text-sm">Invite someone to collaborate</p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((member) => (
              <div 
                key={member.id} 
                className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-zinc-200 rounded-full flex items-center justify-center text-zinc-600 font-medium">
                    {member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900">{member.name}</p>
                    <p className="text-sm text-zinc-500">{member.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {member.is_owner ? (
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                      <Crown className="w-3 h-3 mr-1" />
                      Owner
                    </Badge>
                  ) : (
                    <>
                      <Badge variant="outline" className="text-zinc-500">Member</Badge>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-red-600 hover:text-red-700"
                            data-testid={`remove-member-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove Member?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {member.name} will lose access to this project.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => handleRemoveMember(member.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Remove
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Pending Invitations */}
      <Card className="bg-white border-zinc-200 rounded-xl p-6">
        <h3 className="font-outfit font-semibold text-lg text-zinc-900 mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Pending Invitations ({invites.length})
        </h3>
        
        {invites.length === 0 ? (
          <div className="text-center py-8 text-zinc-500">
            <Mail className="w-10 h-10 mx-auto mb-3 text-zinc-300" />
            <p>No pending invitations</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invites.map((invite) => (
              <div 
                key={invite.id} 
                className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Clock className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-zinc-900">{invite.email}</p>
                    <p className="text-sm text-zinc-500">
                      Expires: {new Date(invite.expires_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="text-orange-600 border-orange-200">
                    Pending
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleResendInvite(invite.id)}
                    disabled={resendingId === invite.id}
                    title="Resend invitation"
                    data-testid={`resend-invite-${invite.id}`}
                  >
                    {resendingId === invite.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleShowEmailContent(invite.id)}
                    title="View email content"
                    data-testid={`view-email-${invite.id}`}
                  >
                    <FileText className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleCopyLink(invite.invite_url)}
                    title="Copy invite link"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-600 hover:text-red-700"
                        data-testid={`cancel-invite-${invite.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Invitation?</AlertDialogTitle>
                        <AlertDialogDescription>
                          The invitation to {invite.email} will be revoked.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={() => handleCancelInvite(invite.id)}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          Revoke
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Email Content Dialog */}
      <Dialog open={emailContentOpen} onOpenChange={setEmailContentOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Invitation Email Content
            </DialogTitle>
            <DialogDescription>
              Copy this content to send the invitation manually
            </DialogDescription>
          </DialogHeader>
          
          {loadingEmailContent ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : emailContent && (
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs text-zinc-500">To:</Label>
                <p className="font-medium">{emailContent.recipient}</p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Subject:</Label>
                <p className="font-medium">{emailContent.subject}</p>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Message:</Label>
                <Textarea 
                  value={emailContent.plain_text}
                  readOnly
                  className="mt-1 h-48 text-sm font-mono"
                />
              </div>
              <div className="bg-zinc-100 p-3 rounded-lg">
                <Label className="text-xs text-zinc-500">Direct Link:</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={emailContent.invite_url}
                    readOnly
                    className="text-xs bg-white"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleCopyLink(emailContent.invite_url)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setEmailContentOpen(false)}
                  className="rounded-lg"
                >
                  Close
                </Button>
                <Button
                  onClick={copyEmailContent}
                  className="bg-primary hover:bg-orange-600 text-white rounded-lg"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Message
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
